/**
 * Router principal de Zen
 */
import { useState, useEffect } from 'react'
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { useAuthStore } from '@/store/authStore'
import { usePermission } from '@/hooks/usePermission'

// Auth pages
import { LoginPage } from '@/modules/auth/components/LoginPage'
import { SelectTenantPage } from '@/modules/auth/components/SelectTenantPage'
import { ForgotPasswordPage } from '@/modules/auth/components/ForgotPasswordPage'
import { ResetPasswordPage } from '@/modules/auth/components/ResetPasswordPage'
import { AcceptInvitationPage } from '@/modules/auth/components/AcceptInvitationPage'
import { GoogleCallbackPage } from '@/modules/auth/components/GoogleCallbackPage'
import { SAMLCallbackPage } from '@/modules/auth/components/SAMLCallbackPage'
import { SAMLErrorPage } from '@/modules/auth/components/SAMLErrorPage'
import { WorkspaceLookupPage } from '@/modules/auth/components/WorkspaceLookupPage'

// Profile
import { ProfilePage } from '@/modules/profile/ProfilePage'
import { ChangePasswordPage } from '@/modules/profile/ChangePasswordPage'

// App pages
import { DashboardPage } from '@/modules/dashboard/DashboardPage'
import { UsersPage } from '@/modules/users/components/UsersPage'
import { AppLayout } from '@/app/layouts/AppLayout'

// Sprint 2
import { ConfigPage } from '@/modules/config/components/ConfigPage'
import { CustomersPage } from '@/modules/sales/components/CustomersPage'
import { SalesRepsPage } from '@/modules/sales/components/SalesRepsPage'
import { ProductsPage } from '@/modules/sales/components/ProductsPage'
import { CategoriesPage } from '@/modules/sales/components/catalog/CategoriesPage'
import { AttributesPage } from '@/modules/sales/components/catalog/AttributesPage'
import { RelationshipsPage } from '@/modules/sales/components/catalog/RelationshipsPage'

// Sprint 3
import { SaleDocumentsPage } from '@/modules/sales/components/SaleDocumentsPage'
import { DocumentFormPage } from '@/modules/sales/components/DocumentFormPage'
import { Document360Page } from '@/modules/sales/components/three60/Document360Page'
import { Invoice360Page } from '@/modules/purchases/components/three60/Invoice360Page'
import { TemplatesPage } from '@/modules/sales/components/TemplatesPage'

// Sprint 6 — Cobranza y Suscripciones
import { CollectionsPage } from '@/modules/sales/components/CollectionsPage'
import { SubscriptionsPage } from '@/modules/sales/components/SubscriptionsPage'
import { ContractsPage } from '@/modules/sales/components/ContractsPage'
import { GuidesPage } from '@/modules/sales/components/GuidesPage'

// PED-01 — Pedidos de Venta, Comisiones, Forecast
import { SalesOrdersPage } from '@/modules/sales/components/SalesOrdersPage'
import { SalesOrderFormPage } from '@/modules/sales/components/SalesOrderFormPage'
import { CommissionDashboardPage } from '@/modules/sales/components/CommissionDashboardPage'
import { ForecastPage } from '@/modules/sales/components/ForecastPage'

// Sprint 7 — Contabilidad
import { AccountingPage } from '@/modules/accounting/components/AccountingPage'
import IntlInvoicesPage from '@/modules/intl_invoices/components/IntlInvoicesPage'
import { tenantSegunHost } from '@/lib/tenantNav'
import { recordarDestino, tomarDestino } from '@/lib/destinoTrasLogin'
import { rutaTrasLogin } from '@/modules/auth/rutaTrasLogin'

// Sprint 10 — Accounting tabs individuales
import { AccountsTab } from '@/modules/accounting/components/AccountsTab'
import { CostCentersTab } from '@/modules/accounting/components/CostCentersTab'
import { VouchersTab } from '@/modules/accounting/components/VouchersTab'
import { ReportsTab } from '@/modules/accounting/components/ReportsTab'
import { BankReconciliationTab } from '@/modules/accounting/components/BankReconciliationTab'
import { AccountingSettingsTab } from '@/modules/accounting/components/AccountingSettingsTab'
import { AccountingAuditTab } from '@/modules/accounting/components/AccountingAuditTab'
import { ExpensesTab } from '@/modules/accounting/components/ExpensesTab'
import { BudgetsTab } from '@/modules/accounting/components/BudgetsTab'
import { FixedAssetsTab } from '@/modules/accounting/components/FixedAssetsTab'
import { DJ1926Page } from '@/modules/accounting/components/DJ1926Page'
import { DJ1866Page } from '@/modules/accounting/components/DJ1866Page'
import { RealEstatePage } from '@/modules/real-estate/components/RealEstatePage'
import { DJ1835Page } from '@/modules/real-estate/components/DJ1835Page'
import { ForeignDebtPage } from '@/modules/treasury/components/ForeignDebtPage'
import { DJ1930Page } from '@/modules/treasury/components/DJ1930Page'
import { PeriodLocksTab } from '@/modules/accounting/components/PeriodLocksTab'
import { FiscalYearTab } from '@/modules/accounting/components/FiscalYearTab'

// Sprint 8 — Tesorería
import { TreasuryPage } from '@/modules/treasury/components/TreasuryPage'

// Sprint 10 — Treasury tabs individuales
import { FactoringEntitiesTab } from '@/modules/treasury/components/FactoringEntitiesTab'
import { FactoringOperationsTab } from '@/modules/treasury/components/FactoringOperationsTab'
import { FactoringReportTab } from '@/modules/treasury/components/FactoringReportTab'

// Cuentas por Cobrar / Pagar
import ReceivablesPage from '@/modules/finance/components/ReceivablesPage'
import ArAgingTab from '@/modules/finance/components/ArAgingTab'
import ArStatementsTab from '@/modules/finance/components/ArStatementsTab'
import CollectionsTab from '@/modules/finance/components/CollectionsTab'
import ArCashFlowTab from '@/modules/finance/components/ArCashFlowTab'
import CustomerCreditsTab from '@/modules/finance/components/CustomerCreditsTab'
import CollectionConfigTab from '@/modules/finance/components/CollectionConfigTab'
import PayablesPage from '@/modules/finance/components/PayablesPage'
import ApAgingTab from '@/modules/finance/components/ApAgingTab'
import ApStatementsTab from '@/modules/finance/components/ApStatementsTab'
import PaymentScheduleTab from '@/modules/finance/components/PaymentScheduleTab'
import ApCashFlowTab from '@/modules/finance/components/ApCashFlowTab'

// Nómina / RRHH
import PayrollPage from '@/modules/payroll/components/PayrollPage'
import EmployeesTab from '@/modules/payroll/components/EmployeesTab'
import ContractsTab from '@/modules/payroll/components/ContractsTab'
import PeriodsTab from '@/modules/payroll/components/PeriodsTab'
import PayslipsTab from '@/modules/payroll/components/PayslipsTab'
import DeclarationsTab from '@/modules/payroll/components/DeclarationsTab'
import VacationsTab from '@/modules/payroll/components/VacationsTab'
import TerminationsTab from '@/modules/payroll/components/TerminationsTab'
import BonusesTab from '@/modules/payroll/components/BonusesTab'
import DeductionsTab from '@/modules/payroll/components/DeductionsTab'
import IndicatorsTab from '@/modules/payroll/components/IndicatorsTab'
import { SettingsTab as PayrollSettingsTab } from '@/modules/payroll/components/SettingsTab'
import OrgChartPage from '@/modules/payroll/components/OrgChartPage'
import DocumentosPage from '@/modules/payroll/components/documentos/DocumentosPage'
import EditorPlantillaPage from '@/modules/payroll/components/documentos/EditorPlantillaPage'
import GenerarDocumentosPage from '@/modules/payroll/components/documentos/GenerarDocumentosPage'
import EnvioDetallePage from '@/modules/payroll/components/documentos/EnvioDetallePage'
import { HonorariosTab } from '@/modules/honorarios/components/HonorariosTab'
import HrAssetsTab from '@/modules/hr_assets/components/HrAssetsTab'

// Inventario
import InventoryPage from '@/modules/inventory/components/InventoryPage'
import StockTab from '@/modules/inventory/components/StockTab'
import MobileScannerPage from '@/modules/inventory/components/MobileScannerPage'
import WarehousesTab from '@/modules/inventory/components/WarehousesTab'
import PosPage from '@/modules/pos/components/PosPage'
import PosStoresTab from '@/modules/pos/components/StoresTab'
import PosTerminalsTab from '@/modules/pos/components/TerminalsTab'
import PosFoliosTab from '@/modules/pos/components/FoliosTab'
import PosSalesTab from '@/modules/pos/components/SalesTab'
import PosOperatorsTab from '@/modules/pos/components/OperatorsTab'
import PosReglasCajaTab from '@/modules/pos/components/ReglasCajaTab'
import PosAgentTab from '@/modules/pos/components/AgentTab'
import PosCashTab from '@/modules/pos/components/CashTab'
import MovementsTab from '@/modules/inventory/components/MovementsTab'
import CountsTab from '@/modules/inventory/components/CountsTab'
import TransfersTab from '@/modules/inventory/components/TransfersTab'

// Sprint 5 — Compras
import { SuppliersPage } from '@/modules/purchases/components/SuppliersPage'
import { PurchaseOrdersPage } from '@/modules/purchases/components/PurchaseOrdersPage'
import { SupplierInvoicesPage } from '@/modules/purchases/components/SupplierInvoicesPage'
import { SupplierPaymentsPage } from '@/modules/purchases/components/SupplierPaymentsPage'
import { PurchasesReportPage } from '@/modules/purchases/components/PurchasesReportPage'

// DTEs Recibidos
import { InboundDTEPage } from '@/modules/dte-inbound/components/InboundDTEPage'

// Platform Admin
import { PlatformAdminPage } from '@/modules/platform/components/PlatformAdminPage'
import { ConsolidatedDashboardPage } from '@/modules/platform/components/ConsolidatedDashboardPage'
import { CertificationPage } from '@/modules/certification/components/CertificationPage'
import { CertificationRunDetailPage } from '@/modules/certification/components/CertificationRunDetailPage'

// Reports Center
import { ReportsCenterPage, ReportDetailPage } from '@/modules/reports/components/ReportsCenterPage'
import { CustomReportPage } from '@/modules/reports/components/CustomReportPage'

// Payments
import PaymentReturnPage from '@/modules/payments/components/PaymentReturnPage'

// Estudio Contable (Portal de cartera)
import { FirmPortfolioPage } from '@/modules/firm/components/FirmPortfolioPage'
import { FirmClientsPage } from '@/modules/firm/components/FirmClientsPage'
import { FirmStaffPage } from '@/modules/firm/components/FirmStaffPage'
import { FirmRequestsPage } from '@/modules/firm/components/FirmRequestsPage'

// ─── Employee Portal (Caso A — portal aislado) ───────────────────────────────
import PortalLayout, { RequirePortalAuth } from '@/modules/employee_portal/layouts/PortalLayout'
import ErpPortalLayout from '@/modules/employee_portal/layouts/ErpPortalLayout'
import PortalLoginPage from '@/modules/employee_portal/pages/PortalLoginPage'
import PortalActivatePage from '@/modules/employee_portal/pages/PortalActivatePage'
import PortalRecoverPasswordPage from '@/modules/employee_portal/pages/PortalRecoverPasswordPage'
import PortalSAMLCallbackPage from '@/modules/employee_portal/pages/PortalSAMLCallbackPage'
import PortalSAMLErrorPage from '@/modules/employee_portal/pages/PortalSAMLErrorPage'
import MyPayslipsPage from '@/modules/employee_portal/pages/MyPayslipsPage'
import MyTimeOffPage from '@/modules/employee_portal/pages/MyTimeOffPage'
import MyDocumentsPage from '@/modules/employee_portal/pages/MyDocumentsPage'
import MySignaturesPage from '@/modules/employee_portal/pages/MySignaturesPage'
import VerificarDocumentoPage from '@/modules/employee_portal/pages/VerificarDocumentoPage'
import MyTeamPage from '@/modules/employee_portal/pages/MyTeamPage'
import PortalOrgChartPage from '@/modules/employee_portal/pages/OrgChartPage'
import PortalHomePage from '@/modules/employee_portal/pages/HomePage'
import PortalProfilePage from '@/modules/employee_portal/pages/MyProfilePage'

// ─── Entry point sin tenant (app.clobi.io) ───────────────────────────────────
// Cuando el frontend se sirve desde `app.<dominio>`, el usuario no tiene un
// tenant asignado — la única ruta válida es `/auth/workspace-lookup`, donde
// ingresa su email y obtiene la lista de workspaces a los que tiene acceso.
function isWorkspaceLookupHost(): boolean {
  const host = window.location.hostname
  return host === 'app.clobi.io' || host.startsWith('app.')
}

// ─── Query Client ─────────────────────────────────────────────────────────────
// 🔴 El cliente es ÚNICO y vive en lib/queryClient (importado arriba). Acá se
// creaba un segundo (el de Sprint 10, sin la red de errores de mutación), y como
// este provider es más interno que el de main.tsx, era el que ganaba: la red del
// incidente del 2026-07-28 quedó inerte durante meses. No volver a crear uno acá.

// ─── Hydration guard ─────────────────────────────────────────────────────────
// Zustand persist carga desde localStorage de forma asíncrona.
// Sin este guard, los auth guards ven isAuthenticated=false durante la hidratación
// y redirigen a /login aunque el usuario estuviera logueado.

/** Lee la cookie zen_session y devuelve los datos de sesión, o null si no existe/es inválida. */
function readSessionCookie(): { isAuthenticated: boolean; user: import('@/store/authStore').AuthUser; tenants: import('@/store/authStore').TenantInfo[] } | null {
  const match = document.cookie.match(/(?:^|;\s*)zen_session=([^;]+)/)
  if (!match) return null
  try {
    return JSON.parse(atob(decodeURIComponent(match[1])))
  } catch { return null }
}

function HydrationGate({ children }: { children: JSX.Element }) {
  const [hydrated, setHydrated] = useState(useAuthStore.persist.hasHydrated())
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (hydrated) return
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true))
    return unsub
  }, [hydrated])

  // Sincronización multi-pestaña: si otra pestaña hace logout, limpiar esta también.
  // El evento 'storage' solo se dispara en pestañas del mismo origen que NO escribieron el cambio.
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'zen-auth') return
      if (!e.newValue) {
        // Clave eliminada → logout en otra pestaña
        useAuthStore.getState().logout()
        window.location.replace('/auth/login')
        return
      }
      try {
        const parsed = JSON.parse(e.newValue)
        if (parsed?.state?.isAuthenticated === false) {
          useAuthStore.getState().logout()
          window.location.replace('/auth/login')
        }
      } catch { /* ignorar */ }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  // Refresh proactivo: al recargar o llegar de otro subdominio, accessToken no está en memoria.
  // 1. Si localStorage está vacío pero zen_session cookie existe → hidratar user/tenants desde cookie.
  // 2. Con isAuthenticated=true y sin accessToken → obtener nuevo token vía httpOnly refresh cookie.
  // 3. Leer ?t=schema para setear activeTenant sin pasar por SelectTenantPage.
  useEffect(() => {
    if (!hydrated) return

    const store = useAuthStore.getState()

    // Cross-domain: localStorage vacío en el nuevo subdominio → leer zen_session cookie
    if (!store.isAuthenticated) {
      const session = readSessionCookie()
      if (session?.user && session?.tenants) {
        store.hydrateFromSession(session)
      }
    }

    const { isAuthenticated, accessToken } = useAuthStore.getState()

    // El HOST manda sobre lo que quedó guardado. `localStorage` es por origen y
    // el switcher escribía el tenant destino en el origen que abandonaba, así que
    // volver a ese subdominio hidrataba con el tenant equivocado (entrar a
    // zenlatam.clobi.io y ver «Marva» en el selector). Solo corrige cuando NO hay
    // `?t=`: ese lo pone el switcher y ya viene con el host que corresponde.
    if (isAuthenticated && !new URLSearchParams(window.location.search).get('t')) {
      const st = useAuthStore.getState()
      const delHost = tenantSegunHost(st.tenants ?? [], st.activeTenantSchema)
      if (delHost) st.setActiveTenant(delHost)
    }

    if (isAuthenticated && !accessToken) {
      // Leer ?t= antes de refrescar para que activeTenant quede seteado a tiempo
      const params = new URLSearchParams(window.location.search)
      const tenantParam = params.get('t')
      // ?sb= preserva el estado del sidebar al cambiar de empresa (localStorage es
      // por-origen). Se escribe ANTES de montar AppLayout, que lo lee en su useState.
      const sbParam = params.get('sb')
      if (sbParam === '0' || sbParam === '1') {
        try { localStorage.setItem('zen-sidebar-open', sbParam) } catch { /* no-op */ }
      }
      if (tenantParam) {
        useAuthStore.getState().setActiveTenant(tenantParam)
      }
      if (tenantParam || sbParam) {
        window.history.replaceState(null, '', window.location.pathname)
      }

      // Refresh COORDINADO entre pestañas (Web Locks) para no pisarse con otras
      // pestañas de la misma sesión: tras un deploy todas recargan y refrescan a la
      // vez, y con ROTATE_REFRESH_TOKENS el que refresca 2º recibía un token ya
      // blacklisteado → 401 → logout espurio. performTokenRefresh serializa el
      // refresh y usa getApiBaseUrl (baseURL absoluta, no relativa vía proxy Vite).
      import('@/lib/apiClient').then(({ performTokenRefresh, shouldLogoutOnRefreshError }) => {
        performTokenRefresh().then(access => {
          useAuthStore.getState().setTokens(access)
          setReady(true)
        }).catch((err) => {
          // Solo desloguear ante un fallo de auth real (401/403). Ante un fallo
          // transitorio (backend caído durante un deploy, red, timeout) mantener la
          // sesión: el interceptor la recupera al volver el backend, sin echar al usuario.
          if (shouldLogoutOnRefreshError(err)) {
            useAuthStore.getState().logout()
          }
          setReady(true)
        })
      })
    } else {
      setReady(true)
    }
  }, [hydrated])

  if (!hydrated || !ready) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }
  return <>{children}</>
}

// ─── Guards ───────────────────────────────────────────────────────────────────

/**
 * Manda al login recordando qué se estaba abriendo, para volver ahí después de
 * entrar (#1656). Sin esto, un enlace de un correo abierto sin sesión terminaba
 * en el dashboard.
 */
function IrAlLogin({ a = '/auth/login' }: { a?: string }) {
  const { pathname, search } = useLocation()
  recordarDestino(pathname + search)
  return <Navigate to={a} replace />
}

function RequireAuth() {
  const { isAuthenticated, isTokenExpired } = useAuthStore()
  if (!isAuthenticated || isTokenExpired()) {
    return <IrAlLogin />
  }
  return <Outlet />
}

function RequireGuest() {
  const { isAuthenticated, activeTenantSchema, user, tenants, isTokenExpired } = useAuthStore()
  if (isAuthenticated && !isTokenExpired()) {
    // ⚠️ Este guard se vuelve a dibujar apenas el login guarda la sesión, ANTES
    // del `onSuccess` del formulario: si no siguiera la misma regla, mandaba al
    // dashboard y se perdía el enlace que se estaba abriendo (#1656).
    if (activeTenantSchema) return <Navigate to={tomarDestino() ?? '/dashboard'} replace />
    if (tenants.length > 0) return <Navigate to={rutaTrasLogin({ tenants, user: user ?? {} })} replace />
    if (user?.is_platform_admin) return <Navigate to="/platform" replace />
    return <Navigate to="/auth/select-tenant" replace />
  }
  return <Outlet />
}

function RequireTenant() {
  const { isAuthenticated, activeTenantSchema, tenants, isTokenExpired } = useAuthStore()
  const location = useLocation()
  if (!isAuthenticated || isTokenExpired()) return <IrAlLogin />
  if (!activeTenantSchema) {
    if (tenants.length > 1) return <IrAlLogin a="/auth/select-tenant" />
    if (tenants.length === 1) {
      // Con una sola empresa no hay nada que elegir: se activa y se sigue a la
      // pantalla pedida (antes saltaba al dashboard y perdía el enlace).
      useAuthStore.getState().setActiveTenant(tenants[0].schema)
      return <Navigate to={location} replace />
    }
    // Platform admin sin tenants → panel de administración
    const user = useAuthStore.getState().user
    if (user?.is_platform_admin) return <Navigate to="/platform" replace />
    return <Navigate to="/auth/no-access" replace />
  }
  return <Outlet />
}

function RequirePlatformAdmin() {
  const { isAuthenticated, user, isTokenExpired } = useAuthStore()
  if (!isAuthenticated || isTokenExpired()) return <IrAlLogin />
  if (!user?.is_platform_admin) return <Navigate to="/dashboard" replace />
  return <Outlet />
}

function RequirePermission(
  { module, action = 'view', anyOf }: { module?: string; action?: string; anyOf?: string[] },
) {
  const { can } = usePermission()
  // anyOf: el subtree es accesible si el usuario tiene CUALQUIERA de los módulos
  // (ej. /payroll alberga módulos separados como `honorarios`/`hr_assets`; un
  // contador con `honorarios` pero sin `payroll` debe poder entrar). La sidebar y
  // las APIs gatean cada sección por su propio módulo.
  const ok = anyOf ? anyOf.some(m => can(m, action)) : can(module!, action)
  if (!ok) return <Navigate to="/dashboard" replace />
  return <Outlet />
}

// Estudio Contable: gating ORTOGONAL al RBAC por-tenant. La señal `firm` (señal
// por-usuario, no por-tenant) se persiste en el store y la rehidrata /auth/me.
function RequireFirm() {
  const { firm } = useAuthStore()
  if (!firm) return <Navigate to="/dashboard" replace />
  return <Outlet />
}

function RequireFirmAdmin() {
  const { firm } = useAuthStore()
  if (!firm || firm.firm_role !== 'firm_admin') return <Navigate to="/dashboard" replace />
  return <Outlet />
}

// Guard por sub-sección de Configuración: lee la sección de la URL, normaliza
// guion→underscore y verifica `config.<section>`. can() hace fallback a 'config',
// así un admin con `config` accede a todas las secciones y un rol con solo
// `config.folios` accede solo a Folios. Sin section (ruta /config raíz) → exige 'config'.
function RequireConfigSection() {
  const { can } = usePermission()
  const { section } = useParams<{ section?: string }>()
  const module = section ? `config.${section.replace(/-/g, '_')}` : 'config'
  if (!can(module, 'view')) return <Navigate to="/dashboard" replace />
  return <Outlet />
}


// ─── Router ───────────────────────────────────────────────────────────────────

// En hostnames del tipo `app.*` solo exponemos la página de workspace-lookup;
// todo lo demás redirige ahí.
const workspaceLookupRouter = createBrowserRouter([
  { path: '/auth/workspace-lookup', element: <WorkspaceLookupPage /> },
  { path: '*', element: <Navigate to="/auth/workspace-lookup" replace /> },
], {
  future: { v7_relativeSplatPath: true },
})

const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/dashboard" replace /> },

  {
    element: <RequireGuest />,
    children: [
      { path: '/auth/login', element: <LoginPage /> },
      { path: '/auth/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },

  {
    element: <RequireAuth />,
    children: [
      { path: '/auth/select-tenant', element: <SelectTenantPage /> },
      { path: '/auth/no-access', element: <NoAccessPage /> },
      { path: '/consolidated', element: <ConsolidatedDashboardPage /> },
    ],
  },

  { path: '/auth/accept-invitation', element: <AcceptInvitationPage /> },
  { path: '/auth/reset-password', element: <ResetPasswordPage /> },
  { path: '/auth/callback/google', element: <GoogleCallbackPage /> },
  { path: '/auth/callback/saml', element: <SAMLCallbackPage /> },
  { path: '/auth/saml-error', element: <SAMLErrorPage /> },

  // Retorno de pago (sin auth — el pagador puede no ser usuario del sistema)
  { path: '/payments/return', element: <PaymentReturnPage /> },

  // Platform Admin (no requiere tenant, solo is_platform_admin)
  {
    element: <RequirePlatformAdmin />,
    children: [
      { path: '/platform', element: <PlatformAdminPage /> },
    ],
  },

  {
    element: <RequireTenant />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/dashboard', element: <DashboardPage /> },
          // Perfil y cambio de contraseña (accesible para todos)
          { path: '/profile', element: <ProfilePage /> },
          { path: '/change-password', element: <ChangePasswordPage /> },

          // Usuarios — requiere users:view
          { element: <RequirePermission module="users" />, children: [
            { path: '/users', element: <UsersPage /> },
          ]},

          // Certificación SII — herramienta interna (solo platform admin),
          // opera el schema del tenant desde su subdominio.
          { element: <RequirePlatformAdmin />, children: [
            { path: '/certification', element: <CertificationPage /> },
            { path: '/certification/:runId', element: <CertificationRunDetailPage /> },
          ]},

          // Configuración — la raíz exige config:view; cada sub-sección verifica
          // su sub-módulo granular config.<section> (con fallback a config).
          { element: <RequirePermission module="config" />, children: [
            { path: '/config', element: <Navigate to="/config/profile" replace /> },
          ]},
          { element: <RequireConfigSection />, children: [
            { path: '/config/:section', element: <ConfigPage /> },
          ]},

          // Ventas
          { path: '/sales', element: <Navigate to="/sales/documents" replace /> },
          { element: <RequirePermission module="sales.customers" />, children: [
            { path: '/sales/customers', element: <CustomersPage /> },
            { path: '/sales/sales-reps', element: <SalesRepsPage /> },
          ]},
          { element: <RequirePermission module="sales.products" />, children: [
            { path: '/sales/products', element: <ProductsPage /> },
            { path: '/sales/catalog/categories', element: <CategoriesPage /> },
            { path: '/sales/catalog/attributes', element: <AttributesPage /> },
            { path: '/sales/catalog/relationships', element: <RelationshipsPage /> },
          ]},
          { element: <RequirePermission module="sales.documents" />, children: [
            { path: '/sales/documents', element: <SaleDocumentsPage /> },
            { path: '/sales/documents/new', element: <DocumentFormPage /> },
            { path: '/sales/documents/:id/edit', element: <DocumentFormPage /> },
            { path: '/sales/documents/:id/360', element: <Document360Page /> },
          ]},
          { element: <RequirePermission module="sales.templates" />, children: [
            { path: '/sales/templates', element: <TemplatesPage /> },
          ]},
          // Redirect: cobranza movida a CxC
          { path: '/sales/collections', element: <Navigate to="/receivables/collections" replace /> },
          { element: <RequirePermission module="sales.subscriptions" />, children: [
            { path: '/sales/subscriptions', element: <SubscriptionsPage /> },
          ]},
          { element: <RequirePermission module="sales.contracts" />, children: [
            { path: '/sales/contracts', element: <ContractsPage /> },
          ]},
          { element: <RequirePermission module="sales.guides" />, children: [
            { path: '/sales/guides', element: <GuidesPage /> },
            { path: '/sales/guides/:id/360', element: <Document360Page /> },
          ]},
          { element: <RequirePermission module="sales.orders" />, children: [
            { path: '/sales/orders', element: <SalesOrdersPage /> },
            { path: '/sales/orders/new', element: <SalesOrderFormPage /> },
            { path: '/sales/orders/:id/edit', element: <SalesOrderFormPage /> },
          ]},
          { element: <RequirePermission module="sales.commissions" />, children: [
            { path: '/sales/commissions', element: <CommissionDashboardPage /> },
          ]},
          { element: <RequirePermission module="sales.forecast" />, children: [
            { path: '/sales/forecast', element: <ForecastPage /> },
          ]},

          // Compras
          { path: '/purchases', element: <Navigate to="/purchases/suppliers" replace /> },
          { element: <RequirePermission module="purchases.suppliers" />, children: [
            { path: '/purchases/suppliers', element: <SuppliersPage /> },
          ]},
          { element: <RequirePermission module="purchases.orders" />, children: [
            { path: '/purchases/orders', element: <PurchaseOrdersPage /> },
          ]},
          { element: <RequirePermission module="purchases.invoices" />, children: [
            { path: '/purchases/invoices', element: <SupplierInvoicesPage /> },
            { path: '/purchases/invoices/:id/360', element: <Invoice360Page /> },
          ]},
          // Redirect: pagos movidos a CxP
          { path: '/purchases/payments', element: <Navigate to="/payables/payments" replace /> },
          { element: <RequirePermission module="reports" />, children: [
            { path: '/purchases/reports', element: <PurchasesReportPage /> },
          ]},
          { element: <RequirePermission module="dte_inbound" />, children: [
            { path: '/purchases/received-dtes', element: <InboundDTEPage /> },
          ]},

          // Contabilidad
          { element: <RequirePermission module="accounting" />, children: [
            {
              path: '/accounting',
              element: <AccountingPage />,
              children: [
                { index: true, element: <Navigate to="accounts" replace /> },
                { path: 'accounts', element: <AccountsTab /> },
                { path: 'cost-centers', element: <CostCentersTab /> },
                { path: 'vouchers', element: <VouchersTab /> },
                { path: 'reports', element: <ReportsTab /> },
                { path: 'reconciliation', element: <BankReconciliationTab /> },
                { path: 'audit', element: <AccountingAuditTab /> },
                { path: 'settings', element: <AccountingSettingsTab /> },
                { path: 'expenses', element: <ExpensesTab /> },
                { path: 'budgets', element: <BudgetsTab /> },
                { path: 'fixed-assets', element: <FixedAssetsTab /> },
                { path: 'dj-1926', element: <DJ1926Page /> },
                { path: 'dj-1866', element: <DJ1866Page /> },
                { path: 'dj-1835', element: <DJ1835Page /> },
                { path: 'real-estate', element: <RealEstatePage /> },
                { path: 'intl-invoices', element: <IntlInvoicesPage /> },
                { path: 'period-locks', element: <PeriodLocksTab /> },
                { path: 'fiscal-years', element: <FiscalYearTab /> },
              ],
            },
          ]},

          // Tesorería
          { element: <RequirePermission module="treasury" />, children: [
            {
              path: '/treasury',
              element: <TreasuryPage />,
              children: [
                { index: true, element: <Navigate to="operations" replace /> },
                { path: 'entities', element: <FactoringEntitiesTab /> },
                { path: 'operations', element: <FactoringOperationsTab /> },
                { path: 'reports', element: <FactoringReportTab /> },
                { path: 'foreign-debts', element: <ForeignDebtPage /> },
                { path: 'dj-1930', element: <DJ1930Page /> },
              ],
            },
          ]},

          // Inventario
          { element: <RequirePermission module="inventory" />, children: [
            {
              path: '/inventory',
              element: <InventoryPage />,
              children: [
                { index: true, element: <Navigate to="stock" replace /> },
                { path: 'stock', element: <StockTab /> },
                { path: 'warehouses', element: <WarehousesTab /> },
                { path: 'movements', element: <MovementsTab /> },
                { path: 'counts', element: <CountsTab /> },
                { path: 'counts/:id/scan', element: <MobileScannerPage /> },
                { path: 'transfers', element: <TransfersTab /> },
              ],
            },
          ]},

          // Punto de venta externo (PLAN_POS_IMPLEMENTACION F1)
          { element: <RequirePermission module="pos" />, children: [
            {
              path: '/pos',
              element: <PosPage />,
              children: [
                { index: true, element: <Navigate to="stores" replace /> },
                { path: 'stores', element: <PosStoresTab /> },
                { path: 'terminals', element: <PosTerminalsTab /> },
                { path: 'folios', element: <PosFoliosTab /> },
                { path: 'sales', element: <PosSalesTab /> },
                { path: 'operators', element: <PosOperatorsTab /> },
                { path: 'rules', element: <PosReglasCajaTab /> },
                { path: 'cash', element: <PosCashTab /> },
                { path: 'agent', element: <PosAgentTab /> },
              ],
            },
          ]},

          // Nómina / RRHH. El subtree alberga módulos separados (honorarios, hr_assets)
          // que un contador puede tener sin `payroll` → guard any-of.
          { element: <RequirePermission anyOf={['payroll', 'honorarios', 'hr_assets', 'payroll.org_chart', 'hr_documents']} />, children: [
            {
              path: '/payroll',
              element: <PayrollPage />,
              children: [
                { index: true, element: <Navigate to="employees" replace /> },
                { path: 'employees', element: <EmployeesTab /> },
                { path: 'contracts', element: <ContractsTab /> },
                // Plantillas de documentos y envíos en lote: usan el permiso de la
                // carpeta digital, el mismo que subir un documento a una ficha.
                { element: <RequirePermission module="hr_documents" />, children: [
                  { path: 'documents', element: <DocumentosPage /> },
                  { path: 'documents/templates/:id', element: <EditorPlantillaPage /> },
                  { path: 'documents/generate/:templateId', element: <GenerarDocumentosPage /> },
                  { path: 'documents/batches/:id', element: <EnvioDetallePage /> },
                ]},
                // El organigrama tiene su propio módulo a propósito: no expone
                // remuneraciones, así que lo puede ver mucha más gente que la que
                // administra nómina.
                { element: <RequirePermission module="payroll.org_chart" />, children: [
                  { path: 'org-chart', element: <OrgChartPage /> },
                ]},
                { path: 'periods', element: <PeriodsTab /> },
                { path: 'payslips', element: <PayslipsTab /> },
                { path: 'vacations', element: <VacationsTab /> },
                { path: 'terminations', element: <TerminationsTab /> },
                { path: 'bonuses', element: <BonusesTab /> },
                { path: 'deductions', element: <DeductionsTab /> },
                { path: 'indicators', element: <IndicatorsTab /> },
                { path: 'declarations', element: <DeclarationsTab /> },
                { path: 'honorarios', element: <HonorariosTab /> },
                { path: 'assets', element: <HrAssetsTab /> },
                { path: 'settings', element: <PayrollSettingsTab /> },
              ],
            },
          ]},

          // Cuentas por Cobrar (cobranza + aging + statements + reminders + cash flow)
          { element: <RequirePermission module="receivables" />, children: [
            {
              path: '/receivables',
              element: <ReceivablesPage />,
              children: [
                { index: true, element: <Navigate to="collections" replace /> },
                { path: 'collections', element: <CollectionsPage /> },
                { path: 'aging', element: <ArAgingTab /> },
                { path: 'statements', element: <ArStatementsTab /> },
                { path: 'reminders', element: <CollectionsTab /> },
                { path: 'credits', element: <CustomerCreditsTab /> },
                { element: <RequirePermission module="receivables.cash_flow" />, children: [
                  { path: 'cash-flow', element: <ArCashFlowTab /> },
                ]},
                { element: <RequirePermission module="receivables.collection_config" />, children: [
                  { path: 'collection-config', element: <CollectionConfigTab /> },
                ]},
              ],
            },
          ]},

          // Cuentas por Pagar (pagos + aging + statements + schedule + cash flow)
          { element: <RequirePermission module="payables" />, children: [
            {
              path: '/payables',
              element: <PayablesPage />,
              children: [
                { index: true, element: <Navigate to="payments" replace /> },
                { path: 'payments', element: <SupplierPaymentsPage /> },
                { path: 'aging', element: <ApAgingTab /> },
                { path: 'statements', element: <ApStatementsTab /> },
                { path: 'schedule', element: <PaymentScheduleTab /> },
                { path: 'cash-flow', element: <ApCashFlowTab /> },
              ],
            },
          ]},

          // Centro de Reportes
          { element: <RequirePermission module="reports" />, children: [
            { path: '/reports', element: <ReportsCenterPage /> },
            { path: '/reports/custom', element: <CustomReportPage /> },
            { path: '/reports/:reportId', element: <ReportDetailPage /> },
          ]},

          // Estudio Contable (Portal de cartera) — gateado por la señal `firm`,
          // NO por RBAC de tenant. Cartera = cualquier miembro; gestión = firm_admin.
          { element: <RequireFirm />, children: [
            { path: '/firm', element: <Navigate to="/firm/portfolio" replace /> },
            { path: '/firm/portfolio', element: <FirmPortfolioPage /> },
            { element: <RequireFirmAdmin />, children: [
              { path: '/firm/clients', element: <FirmClientsPage /> },
              { path: '/firm/staff', element: <FirmStaffPage /> },
              { path: '/firm/requests', element: <FirmRequestsPage /> },
            ]},
          ]},

        ],
      },

      // Mi Portal — Caso B (User ERP con Employee.user linkeado): usa la sesión ERP
      // ya autenticada (sin re-login) pero con layout de portal propio, SIN el
      // sidebar del ERP. Se abre en una pestaña aparte desde el menú de usuario.
      {
        element: <ErpPortalLayout />,
        children: [
          { path: '/portal', element: <PortalHomePage /> },
          { path: '/portal/ficha', element: <PortalProfilePage /> },
          { path: '/portal/payslips', element: <MyPayslipsPage /> },
          { path: '/portal/documents', element: <MyDocumentsPage /> },
          { path: '/portal/time-off', element: <MyTimeOffPage /> },
          { path: '/portal/my-team', element: <MyTeamPage /> },
          { path: '/portal/org-chart', element: <PortalOrgChartPage /> },
          { path: '/portal/signatures', element: <MySignaturesPage /> },
        ],
      },
    ],
  },

  // Verificación pública de un documento firmado — la página del QR.
  // 🔴 Va FUERA de todo layout y de cualquier guard de sesión: quien la abre es
  // un tercero sin cuenta en Clobi. Cuando no existía, el catch-all la mandaba
  // al login, así que el QR le pedía credenciales a quien venía a verificar.
  { path: '/verificar/:token', element: <VerificarDocumentoPage /> },

  // Portal del colaborador — pantallas públicas (sin layout ni nav)
  { path: '/colaboradores/login', element: <PortalLoginPage /> },
  { path: '/colaboradores/activate/:token', element: <PortalActivatePage /> },
  { path: '/colaboradores/recuperar', element: <PortalRecoverPasswordPage /> },
  { path: '/colaboradores/restablecer/:token', element: <PortalActivatePage modo="restablecer" /> },
  { path: '/colaboradores/callback/saml', element: <PortalSAMLCallbackPage /> },
  { path: '/colaboradores/saml-error', element: <PortalSAMLErrorPage /> },

  // Portal del colaborador aislado — Caso A (EmployeeAccount, sin rol ERP)
  // Solo rutas autenticadas dentro del layout con header + nav.
  {
    path: '/colaboradores',
    element: <PortalLayout />,
    children: [
      {
        element: <RequirePortalAuth><Outlet /></RequirePortalAuth>,
        children: [
          { index: true, element: <PortalHomePage /> },
          { path: 'ficha', element: <PortalProfilePage /> },
          { path: 'payslips', element: <MyPayslipsPage /> },
          { path: 'documents', element: <MyDocumentsPage /> },
          { path: 'time-off', element: <MyTimeOffPage /> },
          { path: 'my-team', element: <MyTeamPage /> },
          { path: 'org-chart', element: <PortalOrgChartPage /> },
          { path: 'signatures', element: <MySignaturesPage /> },
        ],
      },
    ],
  },

  { path: '*', element: <Navigate to="/dashboard" replace /> },
], {
  future: {
    v7_relativeSplatPath: true,
  },
})

// ─── Placeholders ─────────────────────────────────────────────────────────────

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="p-6 text-center text-content-subtle">
      <div className="text-4xl mb-3">🚧</div>
      <h2 className="text-lg font-semibold text-content-muted">{title}</h2>
      <p className="text-sm mt-1">En construcción — Sprint próximo</p>
    </div>
  )
}

function NoAccessPage() {
  const { logout, user } = useAuthStore()
  const navigate = useNavigate()
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-muted">
      <div className="text-center max-w-sm p-6">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="text-xl font-semibold text-content mb-2">Sin acceso</h2>
        <p className="text-content-muted text-sm mb-6">
          No tienes acceso a ninguna empresa activa. Contacta al administrador.
        </p>
        <div className="flex flex-col gap-2">
          {user?.is_platform_admin && (
            <button onClick={() => navigate('/platform')}
              className="px-6 py-2 bg-info-fill text-white rounded-lg text-sm hover:bg-info-fill-hover transition-colors">
              Ir al Panel de Plataforma
            </button>
          )}
          <button onClick={logout}
            className="px-6 py-2 bg-primary text-on-primary rounded-lg text-sm hover:bg-primary-hover transition-colors">
            Volver al inicio
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── App ──────────────────────────────────────────────────────────────────────

export function App() {
  // En `app.<dominio>` el usuario no tiene tenant — saltamos HydrationGate
  // (no hay sesión que hidratar) y servimos solo el router de workspace-lookup.
  if (isWorkspaceLookupHost()) {
    return (
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={workspaceLookupRouter} future={{ v7_startTransition: true }} />
      </QueryClientProvider>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationGate>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </HydrationGate>
    </QueryClientProvider>
  )
}

// Alias para compatibilidad con main.tsx
export { App as AppRouter }
